import { Button, Tooltip } from "antd";
import { useMemo, useState } from "react";
import { EditOutlined, MailOutlined, PlusOutlined } from "@ant-design/icons";
import { StyledTable } from "./styles";
import {
  formatWorkingHours,
  getFullName,
  getTimezoneLabel,
} from "../../../utils/shared";
import Avatar from "../../../components/generic/Avatar";
import EmailRoutingModal from "./EmailRoutingModal";
import { useTenantTopics } from "../../../hooks/useTenantTopics";
import EmailRulesModal from "./EmailRulesModal";

const BranchTable = (props) => {
  const { topics } = useTenantTopics();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const { branches, showModal, loading } = props;

  const columns = useMemo(
    () => [
      {
        title: "Branch Details",
        dataIndex: "name",
        key: "branch_details",
        width: 300,
        render: (name, record) => (
          <div className="branch-details-cell">
            <div className="branch-name">{name}</div>
            <div className="branch-email">
              <span>✉</span>
              {record.email}
            </div>
            <div className="branch-code">{record.branch_code}</div>
          </div>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: "Location & Time",
        dataIndex: "address",
        key: "location",
        width: 300,
        render: (address, record) => (
          <div className="location-cell">
            <div className="location-address">{address}</div>
            <div className="location-city">
              {record.city}, {record.postal_code}
            </div>
            <div className="location-timezone">
              <span className="timezone-badge">
                {getTimezoneLabel(record.timezone)}
              </span>
              <span className="timezone-hours">
                <span>🕐</span>
                {formatWorkingHours(record.working_hours)}
              </span>
            </div>
          </div>
        ),
      },
      {
        title: "Manager",
        dataIndex: "branchManger",
        key: "manager",
        width: 250,
        render: (manager) => {
          return (
            <div className="manager-cell">
              <Avatar className="manager-avatar" user={manager} size={40} />
              <div className="manager-info">
                <div className="manager-name">
                  {getFullName(manager) || "N/A"}
                </div>
                <div className="manager-role">{manager?.role}</div>
              </div>
            </div>
          );
        },
        sorter: (a, b) =>
          a.branchManger?.first_name.localeCompare(b.branchManger?.first_name),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 150,
        render: (status) => {
          const statusMap = {
            active: {
              class: "status-active",
              label: "Active",
              dot: "status-dot-active",
            },
            inactive: {
              class: "status-inactive",
              label: "Inactive",
              dot: "status-dot-inactive",
            },
          };

          const statusConfig = statusMap[status] || statusMap.active;

          return (
            <span className={statusConfig.class}>
              <span className={`status-dot ${statusConfig.dot}`}></span>
              {statusConfig.label}
            </span>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 200,
        render: (_, record) => (
          <div className="actions-cell">
            <Button
              className="btn-edit"
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
            >
              Edit
            </Button>

            <Tooltip title="View Email Routings">
              <Button
                color="primary"
                variant="outlined"
                icon={<MailOutlined />}
                onClick={() => setSelectedBranch(record)}
              />
            </Tooltip>
            <Tooltip title="Configure Email Routing">
              <Button
                color="primary"
                variant="outlined"
                icon={<PlusOutlined />}
                onClick={() => setSelectedBranch({ ...record, type: "create" })}
              />
            </Tooltip>
          </div>
        ),
      },
    ],
    [showModal],
  );

  return (
    <>
      <StyledTable
        columns={columns}
        dataSource={branches}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: "max-content" }}
      />
      {selectedBranch &&
        (selectedBranch.type === "create" ? (
          <EmailRoutingModal
            onCancel={() => setSelectedBranch(null)}
            selectedBranch={selectedBranch}
            topics={topics}
          />
        ) : (
          <EmailRulesModal
            onCancel={() => setSelectedBranch(null)}
            selectedBranch={selectedBranch}
            topics={topics}
          />
        ))}
    </>
  );
};

export default BranchTable;
